/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PDFDocument, PDFTextField } from "pdf-lib";
import { getWidgetRectangle } from "components/Forms/utils/pdfFieldGeometry";
import { Document, Page } from "react-pdf";
import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import { CLONE_FIELD_ID, CLONE_FIELD_PREFIX, clientFieldsConfig } from "./utils";
import FieldMappingPanel from "./FieldMappingPanel";
import { DEFAULT_OVERFLOW_POLICY } from "components/Forms/utils/overflowPolicy";
import { useForceMenuCollapsed } from "components/Menu/MenuContext";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { notifyError } from "redux/actions/Notifications";

// Keep in sync with .field-mapping-panel width in _forms.scss
const MAPPING_PANEL_WIDTH = 400;

const Errors = {
    NoPdfUrl: "No PDF file provided.",
    PdfProtected: "PDF file is protected.",
    PdfLoadFailed: "Unable to load file.",
};

const FormFieldsMapping = ({ mode, pdfUrl, value = [], onChange, onError, error }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
    const pdfScale = isMediumScreen ? 1 : 1.2;
    const pdfWidth = isMediumScreen ? 720 : 800;
    const isAdding = mode === "add";
    const [fields, setFields] = useState({});
    // { [pdfField]: /MaxLen } of the text fields that have a character limit
    const [fieldMaxLengths, setFieldMaxLengths] = useState({});
    const [numPages, setNumPages] = useState(null);
    const [values, setValues] = useState(value);
    const [selectedField, setSelectedField] = useState(null);
    const [isCloning, setIsCloning] = useState(false);
    const [fieldInfoVisible, setFieldInfoVisible] = useState(false);
    const [cloningCallback, setCloningCallback] = useState(() => {});

    const startCloning = ({ callback }) => {
        setIsCloning(true);
        setCloningCallback(() => callback);
    };

    useEffect(() => {
        if (pdfUrl) {
            onError(null);
            prepareFields();
        } else {
            onError(Errors.NoPdfUrl);
            setFields({});
            setNumPages(null);
            setValues([]);
            setSelectedField(null);
        }
    }, [pdfUrl, pdfScale, pdfWidth]);

    useEffect(() => {
        setValues(value);
    }, [value]);

    useEffect(() => {
        onChange(values);
    }, [values]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const prepareFields = async () => {
        try {
            const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });
            if (pdfDoc.isEncrypted) {
                dispatch(notifyError(Errors.PdfProtected));
                onError(Errors.PdfProtected);
                return;
            }
            const form = pdfDoc.getForm();
            const pages = pdfDoc.getPages();
            const fieldData = pages.reduce((map, _, idx) => {
                map[idx] = [];
                return map;
            }, {});
            const fields = form.getFields();
            setFieldMaxLengths(
                fields.reduce((acc, field) => {
                    const maxLength = field instanceof PDFTextField ? field.getMaxLength() : undefined;
                    if (maxLength > 0) acc[field.getName()] = maxLength;
                    return acc;
                }, {})
            );
            const isBarcodeField = (field) => {
                return field instanceof PDFTextField && field.acroField.getFullyQualifiedName().includes("PDF417BarCode");
            };
            fields
                .filter((f) => !f.isReadOnly() || (f.isReadOnly() && !isBarcodeField(f)))
                .forEach((field) => {
                    if (isAdding) {
                        // Set mapping values with null initially
                        setValues((prev) => {
                            return [
                                ...prev,
                                { id: null, formId: null, pdfField: field.getName(), type: "string", htmlField: "", clientField: null },
                            ];
                        });
                    }

                    // Prepare field widgets
                    const widgets = field.acroField.getWidgets();
                    widgets.forEach((widget) => {
                        const pageIndex = pages.findIndex((p) => p.ref === widget.P());
                        const { width: pageWidth, height: pageHeight } = pages[pageIndex].getSize();
                        const scale = (pdfScale * pdfWidth) / pageWidth;
                        const rect = getWidgetRectangle(widget);
                        const cssTop = (pageHeight - rect.y - rect.height) * scale;
                        const cssLeft = rect.x * scale;
                        const cssWidth = rect.width * scale;
                        const cssHeight = rect.height * scale;
                        fieldData[pageIndex].push(({ valueGetter, onChange, isSelected, isCloning, cloningCallback, fieldInfoVisible }) => {
                            return (
                                <FieldComponent
                                    value={valueGetter(field.getName())}
                                    onChange={(value) => onChange(field.getName(), value)}
                                    top={cssTop}
                                    left={cssLeft}
                                    width={cssWidth}
                                    height={cssHeight}
                                    onClick={() => {
                                        if (isCloning) {
                                            cloningCallback(field.getName());
                                            setIsCloning(false);
                                        } else {
                                            setSelectedField(field.getName());
                                        }
                                    }}
                                    selected={isSelected(field.getName())}
                                    isCloning={isCloning}
                                    fieldInfoVisible={fieldInfoVisible}
                                    logicFieldName={field.getName()}
                                />
                            );
                        });
                    });
                });

            setFields(fieldData);
        } catch (error) {
            console.error(error);
            onError(Errors.PdfLoadFailed);
            dispatch(notifyError(Errors.PdfLoadFailed));
        }
    };

    const FieldComponent = ({
        selected,
        value,
        onClick,
        top,
        left,
        width,
        height,
        isCloning,
        fieldInfoVisible,
        logicFieldName,
    }) => {
        let iconName = "pencil";
        let iconColor = "secondary";
        if (value) {
            iconName = "rounded-check";
            iconColor = "success";
        } else if (isCloning) {
            if (!selected) {
                iconName = "link";
                iconColor = "secondary";
            }
        }

        const mapped = Boolean(value);

        let classesToApply = { unmapped: !mapped, mapped, selected };
        if (isCloning) {
            classesToApply = {
                cloning: true,
                selected,
                ...(selected ? { "selected-disabled": true } : { "to-be-cloned": true }),
            };
        }

        return (
            <div
                data-field-name={logicFieldName}
                className={clsx("field-component", classesToApply)}
                style={{
                    top,
                    left,
                    width,
                    height,
                }}
                onClick={() => {
                    onClick();
                    setFieldInfoVisible(selected ? !fieldInfoVisible : true);
                }}
                title={String(value || "")}
            >
                <Icon name={iconName} size="sm" color={iconColor} />
            </div>
        );
    };

    const getFieldValue = (fieldName) => {
        return values.find((x) => x.pdfField === fieldName)?.clientField;
    };

    const setFieldValue = (fieldName, value) => {
        setValues((prev) => {
            const idx = prev.findIndex((x) => x.pdfField === fieldName);
            let type = "string";
            const clientFieldConfig = clientFieldsConfig.find((x) => x.id === value);
            if (clientFieldConfig?.type === "date") {
                type = "string_date";
            } else if (clientFieldConfig?.type === "checkbox") {
                type = "checkbox";
            }
            if (idx === -1) {
                return [...prev, { id: null, formId: null, pdfField: fieldName, type, htmlField: "", clientField: value }];
            } else {
                const newValues = [...prev];
                newValues[idx].clientField = value;
                newValues[idx].type = type;
                return newValues;
            }
        });
    };

    // The default policy is stored as null, so mappings that never changed it stay as they were
    const setFieldOverflowPolicy = (fieldName, policy) => {
        const overflowPolicy = policy === DEFAULT_OVERFLOW_POLICY ? null : policy;
        setValues((prev) => {
            const idx = prev.findIndex((x) => x.pdfField === fieldName);
            if (idx === -1) {
                return [
                    ...prev,
                    { id: null, formId: null, pdfField: fieldName, type: "string", htmlField: "", clientField: null, overflowPolicy },
                ];
            }
            const newValues = [...prev];
            newValues[idx] = { ...newValues[idx], overflowPolicy };
            return newValues;
        });
    };

    const closeMappingPanel = useCallback(() => {
        setSelectedField(null);
        setIsCloning(false);
        setCloningCallback(() => {});
        setFieldInfoVisible(false);
    }, []);

    const cancelCloning = useCallback(() => {
        setIsCloning(false);
        setCloningCallback(() => {});
    }, []);

    const locateField = (fieldName) => {
        const fieldElement = document.querySelector(`[data-field-name="${CSS.escape(fieldName)}"]`);
        if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: "instant", block: "center" });
            fieldElement.classList.add("selected-from-mapping");
            setTimeout(() => {
                fieldElement.classList.remove("selected-from-mapping");
            }, 3000);
        }
    };

    const handleMappingSelect = (clientFieldId) => {
        if (clientFieldId === CLONE_FIELD_ID) {
            const targetField = selectedField;
            startCloning({
                callback: (clonedFieldName) => setFieldValue(targetField, `${CLONE_FIELD_PREFIX}${clonedFieldName}`),
            });
        } else {
            setFieldValue(selectedField, clientFieldId);
        }
    };

    // How many other PDF fields already use each client field
    const usage = useMemo(
        () =>
            values.reduce((acc, mapping) => {
                if (mapping.clientField && mapping.pdfField !== selectedField) {
                    acc[mapping.clientField] = (acc[mapping.clientField] || 0) + 1;
                }
                return acc;
            }, {}),
        [values, selectedField],
    );

    const isPanelOpen = Boolean(selectedField) && fieldInfoVisible;

    // Give the PDF more room while the panel is open; the menu returns to its previous state on close
    useForceMenuCollapsed(isPanelOpen);

    // The mapping panel is a fixed drawer over the right edge of the screen: reserve that overlap
    // inside the PDF viewer so no field ends up hidden behind it
    const previewRef = useRef(null);
    useEffect(() => {
        const preview = previewRef.current;
        if (!preview) return;
        if (!isPanelOpen) {
            preview.style.removeProperty("--mapping-panel-overlap");
            return;
        }
        const updateOverlap = () => {
            const drawerLeft = window.innerWidth - Math.min(MAPPING_PANEL_WIDTH, window.innerWidth);
            const overlap = Math.max(0, preview.getBoundingClientRect().right - drawerLeft);
            preview.style.setProperty("--mapping-panel-overlap", `${overlap}px`);
        };
        updateOverlap();
        // The main menu collapses with an animation, which also resizes the preview
        const observer = new ResizeObserver(updateOverlap);
        observer.observe(preview);
        window.addEventListener("resize", updateOverlap);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateOverlap);
        };
    }, [isPanelOpen]);

    if (error) {
        return (
            <div className="pdf-load-error">
                <div>
                    {error === Errors.PdfProtected ? (
                        <Icon name="lock" size="xxlg" color="error" />
                    ) : (
                        <Icon name="no-file" size="xxlg" color="error" />
                    )}
                </div>
                <div>{error}</div>
            </div>
        );
    }

    return (
        <div ref={previewRef} className={clsx("form-preview", { "with-mapping-panel": isPanelOpen })}>
            {!error && (
                <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<LoadingPage />}>
                    {Array.from(new Array(numPages), (el, pageIndex) => (
                        <Page key={`page_${pageIndex + 1}`} pageNumber={pageIndex + 1} scale={pdfScale} width={pdfWidth}>
                            {fields[String(pageIndex)]?.map((component, fieldIndex) => {
                                const Component = component;
                                return (
                                    <Component
                                        key={`page_${pageIndex}_field_${fieldIndex}`}
                                        valueGetter={(fieldName) => getFieldValue(fieldName)}
                                        onChange={setFieldValue}
                                        isSelected={(fieldName) => fieldName === selectedField}
                                        isCloning={isCloning}
                                        cloningCallback={cloningCallback}
                                        fieldInfoVisible={fieldInfoVisible}
                                    />
                                );
                            })}
                        </Page>
                    ))}
                </Document>
            )}
            {isPanelOpen && (
                <FieldMappingPanel
                    pdfFieldName={selectedField}
                    value={getFieldValue(selectedField)}
                    maxLength={fieldMaxLengths[selectedField]}
                    overflowPolicy={values.find((x) => x.pdfField === selectedField)?.overflowPolicy}
                    onOverflowPolicyChange={(policy) => setFieldOverflowPolicy(selectedField, policy)}
                    usage={usage}
                    isCloning={isCloning}
                    onSelect={handleMappingSelect}
                    onRemove={() => setFieldValue(selectedField, null)}
                    onLocate={locateField}
                    onCancelCloning={cancelCloning}
                    onClose={closeMappingPanel}
                />
            )}
        </div>
    );
};

export default FormFieldsMapping;
